import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar, ExternalLink, Heart, Barcode } from "lucide-react";
import { cn } from "@/lib/utils";
import { RiskBadge } from "./RiskBadge";
import { SeverityBadge } from "./SeverityBadge";
import { ImageWithFallback } from "./ImageWithFallback";
import { getOfficialSourceUrl, getRelativeTimeString, getValidImageUrls, getThumbnailUrl, getSeverityBorderColor, parseProductIdentification } from "@/lib/recallUtils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";


export interface Recall {
  id: string;
  productName: string;
  brand: string;
  riskType: 'listeria' | 'salmonella' | 'toxoplasma' | 'ecoli' | 'staphylococcus' | 'bacillus_cereus' | 'campylobacter' | 'clostridium_botulinum' | 'clostridium_perfringens' | 'norovirus' | 'rotavirus' | 'pathogene_autre' | 'mycotoxine_aflatoxine' | 'mycotoxine_ochratoxine' | 'pesticide_eto' | 'chimique_moah_pah' | 'conditionnement';
  severity: 'high' | 'medium' | 'low';
  pregnancyFlag: boolean;
  publishedAt: string;
  category: 'baby-food' | 'dairy' | string;
  batchLot?: string;
  dlc?: string;
  distributionArea?: string[];
  retailer?: string[];
  actionRequired?: string;
  motive?: string;
  images?: string[];
  gtin?: string;
  officialUrl?: string;
  source_url?: string;
  category_id?: string;
  applicability?: {
    pregnancy: boolean;
    baby: boolean;
    mother: boolean;
    extendedRisk?: boolean;
  };
}

interface RecallCardProps {
  recall: Recall;
  onClick?: () => void;
  isPriority?: boolean;
}

export const RecallCard = ({ recall, onClick, isPriority = false }: RecallCardProps) => {
  const queryClient = useQueryClient();

  // Prefetch on hover for instant navigation
  const handleMouseEnter = useCallback(() => {
    // Only prefetch if not already in cache
    const cached = queryClient.getQueryData(['recall', recall.id]);
    if (!cached) {
      queryClient.setQueryData(['recall', recall.id], {
        id: recall.id,
        product_name: recall.productName,
        brand: recall.brand,
        risk_type: recall.riskType,
        severity_score: recall.severity === 'high' ? 5 : recall.severity === 'medium' ? 3 : 1,
        pregnancy_flag: recall.pregnancyFlag,
        batch_lot: recall.batchLot || null,
        dlc_ddm: recall.dlc || null,
        distribution_area: recall.distributionArea,
        retailer: recall.retailer,
        action_required: recall.actionRequired || null,
        published_at: recall.publishedAt,
        category_id: recall.category_id,
        images: recall.images,
        gtin: recall.gtin || null,
        official_url: recall.officialUrl || null,
        source_url: recall.source_url || null,
        applicability: recall.applicability
      });
    }
  }, [queryClient, recall]);

  if (!recall) return null;

  // Parse batch/lot info
  const parsedBatches = parseProductIdentification(recall.batchLot);
  const parsedBatch = parsedBatches.length > 0 ? parsedBatches[0] : null;
  const displayGtin = recall.gtin || parsedBatch?.gtin;

  // Get official source URL using utility function
  const officialUrl = getOfficialSourceUrl(recall);
  
  // Get valid images
  const validImages = getValidImageUrls(recall.images);

  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md border-l-4 h-[210px]",
        getSeverityBorderColor(recall.severity),
        "bg-card hover:bg-accent/5 hover:scale-[1.01]"
      )}
      style={{ contentVisibility: 'auto' } as React.CSSProperties}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      role="article"
      tabIndex={0}
      aria-label={`Rappel : ${recall.productName} par ${recall.brand} — ${recall.severity === 'high' ? 'Risque élevé' : recall.severity === 'medium' ? 'Risque moyen' : 'Risque faible'}`}
      data-testid="recall-card"
      data-published-at={recall.publishedAt}
      data-recall-id={recall.id}
    >
      <CardContent className="p-3 h-full flex flex-col">
        {/* Main content row - image left, all info right */}
        <div className="flex gap-3 flex-1 min-h-0">
          {/* Product Image - always reserve space */}
          <div className="shrink-0 w-20 h-[60px] rounded-lg overflow-hidden bg-muted ring-1 ring-border self-start">
            {validImages.length > 0 ? (
              <ImageWithFallback
                src={getThumbnailUrl(validImages[0])}
                alt={recall.productName}
                className="w-full h-full object-cover"
                loading={isPriority ? "eager" : "lazy"}
                fetchPriority={isPriority ? "high" : "auto"}
                width={80}
                height={60}
              />
            ) : (
              <div className="w-full h-full" />
            )}
          </div>
          
          <div className="flex-1 min-w-0 flex flex-col space-y-1">
            {/* Badges */}
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <SeverityBadge level={recall.severity} />
              <RiskBadge riskType={recall.riskType} motive={recall.motive} />
              
              {recall.category_id === '25' && (
                <Badge variant="outline" className="text-xs shrink-0">
                  👶 Bébé
                </Badge>
              )}
              
              {(recall.pregnancyFlag || recall.applicability?.pregnancy) && (
                <Badge variant="secondary" className="text-xs bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-200 shrink-0">
                  <Heart className="h-3 w-3 mr-1" />
                  Grossesse
                </Badge>
              )}
            </div>

            {/* Product name - 1 line */}
            <h3 className="text-sm font-semibold leading-tight line-clamp-1 text-card-foreground">
              {recall.productName}
            </h3>
            
            {/* Brand + lot */}
            <p className="text-xs text-muted-foreground line-clamp-1">
              <span className="font-medium">{recall.brand}</span>
              {parsedBatch?.lot && <span className="ml-2">• Lot: {parsedBatch.lot}</span>}
            </p>

            {/* GTIN */}
            <div className="h-4 flex items-center">
              {displayGtin && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Barcode className="h-3 w-3" />
                  <span>{displayGtin}</span>
                </p>
              )}
            </div>

            {/* Retailers - single line */}
            <div className="h-6 overflow-hidden">
              {recall.retailer && recall.retailer.length > 0 && (() => {
                const splitRetailers = recall.retailer.flatMap((r) => r.split(/[¤;]|(?:\s-\s)/).map(s => s.trim()).filter(Boolean));
                const maxVisible = 2;
                const remaining = splitRetailers.length - maxVisible;
                return (
                  <div className="flex flex-nowrap gap-1 overflow-hidden">
                    {splitRetailers.slice(0, maxVisible).map((retailer, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs bg-secondary/50 text-secondary-foreground font-medium whitespace-nowrap shrink-0"
                      >
                        {retailer}
                      </span>
                    ))}
                    {remaining > 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs bg-secondary/50 text-secondary-foreground font-medium whitespace-nowrap shrink-0">
                        +{remaining}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Footer: timestamp + source - pushed to bottom */}
        <div className="flex items-center justify-between gap-3 pt-1.5 border-t mt-auto">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span data-testid="recall-published-at">{getRelativeTimeString(recall.publishedAt)}</span>
          </div>
          
          {officialUrl ? (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs shrink-0 px-2"
              onClick={(e) => {
                e.stopPropagation();
                window.open(officialUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Source
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground cursor-help">
                    Source indisponible
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">L'URL source n'est pas disponible pour ce rappel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
};